import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAnchoredList } from '../../components/ui/useAnchoredList';
import { useNavigation } from '../layout/navigation';
import { getLocation } from './api';

/**
 * A place, as somewhere to go and as the chain that identifies it.
 *
 * The name on its own identifies nothing: "3 полка" exists in every cabinet in
 * the workshop, and a unit that says it is on one of them has told you almost
 * nothing. The chain is what makes the answer usable, and it is too long to sit
 * in a table cell — so it appears on hover, where it costs no width at all.
 *
 * Fetched on first hover and kept: a panel can list forty units in a dozen
 * places, and asking for every chain up front to fill in a tooltip nobody has
 * pointed at is forty requests for nothing.
 */
export function LocationLink({
  locationId,
  name,
}: {
  locationId: string | null;
  name: string | null;
}) {
  const { open } = useNavigation();
  const [hovering, setHovering] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const position = useAnchoredList(anchor, hovering);

  const detail = useQuery({
    queryKey: ['locations', 'detail', locationId],
    queryFn: () => getLocation(locationId as string),
    enabled: hovering && Boolean(locationId),
    staleTime: 5 * 60_000,
  });

  if (!locationId) return <>{name ?? '—'}</>;

  const chain = [...(detail.data?.breadcrumbs ?? []), ...(detail.data ? [detail.data] : [])];

  return (
    <span
      ref={anchor}
      className="location-link"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <button type="button" className="link" onClick={() => open('location', locationId)}>
        {name ?? detail.data?.name ?? locationId}
      </button>
      {hovering && chain.length > 0 && (
        <span className="location-link__chain" style={position} role="tooltip">
          {chain.map((step, index) => (
            <span key={step.id}>
              {index > 0 && <em> → </em>}
              {step.name}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
